import { Select } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getSteps } from "../../api/courses.api";
import { getEnrolledStudentByWeek } from "../../api/statistics.api";
import Button from "../../components/Button";
import SimpleHeader from "../../components/layout/SimpleHeader";
import MultiCampusModal from "../../components/statistics/regular-test/modals/MultiCampusModal";
import ChartContainer from "../../components/statistics/student-enrolled-week/ChartContainer";
import TableContainer from "../../components/statistics/student-enrolled-week/TableContainer";
import TabItem from "../../components/statistics/TabItem";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import { IGetSteps } from "../../models/courses.model";
import { IEnrolledStudentByPeriodParams } from "../../models/statistics.model";
import { RootState } from "../../store/rootReducer";
import { getReverseYears } from "../../lib/util";
import useLoader from "../../hooks/useLoader";
import EduStepSelect from "../../components/common/EduStepSelect";
import LevelSelect from "../../components/statistics/LevelSelect";
import moment from "moment";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";
import TableEmpty from "../../components/common/TableEmpty";
import SelectCampusMultiChoice from "../../components/SelectCampusMultiChoice";

enum Contents {
  Table,
  Chart,
}

const StatisticsStudentEnrollWeek = () => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0005 = 연도
  const SY0005 = commonCodes.find(item => item.commonCode === 'SY0005')?.detailCode;
  // PR0002 = 학기유형
  const PR0002 = commonCodes.find((item) => item.commonCode === 'PR0002')?.detailCode;  
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSteps>();
  const { arr: res2, isLoading: isLoading2, error: error2, executeArr: execute2 } = useAxios<any>();
  /*
  useLoader(
    isLoading || isLoading2,
    [error, error2],
  );
  */

  const [content, setContent] = useState(Contents.Table);
  const [learningYearCode, setLearningYearCode] = useState<string>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [levelCode, setLevelCode] = useState<number>();
  const [courseCode, setCourseCode] = useState<number>();
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [semesterType, setSemesterType] = useState("RG");

  useEffect(() => {
    execute(getSteps());

    defSelectBoxSetting();
  }, []);

  const defSelectBoxSetting = () => {
    //검색 필드 Default값 셋팅
    setLearningYearCode(moment().format('YYYY'));
    const defSelValue:any = "";
    setEduStepCode(defSelValue);
    setLevelCode(defSelValue);
    setCourseCode(defSelValue);
  }

  const onSearch = () => {
    if (!learningYearCode) {
      return infoNotification(t('student.student.message30'));
    }

    const req: IEnrolledStudentByPeriodParams = {
      learningYearCode,
      levelCode,
      courseCode,
      campusCode: auth.hqYn === 'Y'
        ? campusCodes
        : (campusCodes.length ? campusCodes : auth.campus),
    };
    if (eduStepCode) {
      req.eduStepCode = eduStepCode;
    }
    const prevYearCode = String(Number(learningYearCode) - 1);
    if (semesterType) {
      req.semesterType = semesterType;
    }

    execute2([
      getEnrolledStudentByWeek(req),
      getEnrolledStudentByWeek({ ...req, learningYearCode: prevYearCode }),
    ]);
  }

  const renderContent = () => {
    switch (content) {
      case Contents.Table:
        return (
          <TableContainer
            res={res2}
            learningYearCode={learningYearCode}
          />
        );
      case Contents.Chart:
        // @ts-ignore
        return <ChartContainer data={res2?.at(0)?.data} />;
    }
  }

  return (
    <>
      <SimpleHeader
        iconFile="icon-statistics-big.svg"
        parentMenu={t('common.menu.statistics')}
        currentMenu={t('statistics.studentEnrollWeek.title1')}
        onlyTitle={true}
      />
      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label> {/* 캠퍼스 */}
              <SelectCampusMultiChoice 
                placeholder={t('statistics.eLearning.title2')}
                onChange={setCampusCodes}
                value={campusCodes}
              />
              <label>
                ({campusCodes.length ? `${campusCodes.length}${t('statistics.attendance.text1')}` : t('common.text.all')})
              </label>
            </li>
            <li>
              <label>{t('statistics.refund.placeholder1')}</label> {/* 연도 */}
              <Select
                placeholder={t('statistics.refund.placeholder1')}
                value={learningYearCode}
                onChange={value => setLearningYearCode(value)}
              >
                {getReverseYears(SY0005).map(item => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </li>
            <li>
              <label>
                {t('program.semester.column11')}
              </label>
              <Select
                className="w-120p"
                value={semesterType}
                onChange={value => setSemesterType(value)}
              >
                {
                  PR0002?.map(({korName: label, detailCode: value}) => {
                    return (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    )
                  })
                }
              </Select>
            </li>            
            <li>
              <label>{t('common.placeholder.1')}</label> {/* 교육단계 */}
              <EduStepSelect
                eduStepCode={eduStepCode}
                setEduStepCode={value => {
                  setEduStepCode(value);
                  const defAllValue:any = "";
                  setLevelCode(defAllValue);
                }}
                width={90}
                includeAll
              />
            </li>
            <li>
              <label>{t('student.student.column26')}</label> {/* 레벨 */}
              <div>
                <LevelSelect
                  levelCode={levelCode}
                  setLevelCode={value =>{
                    setLevelCode(value);

                    const defSelValue:any = "";
                    setCourseCode(defSelValue);
                  }}
                  data={res?.data}
                  eduStepCode={eduStepCode}
                />
              </div>
            </li>
            <li>
              <label>{t('student.student.column27')}</label> {/* 단계 */}
              <Select
                placeholder={t('student.student.column27')}
                value={courseCode}
                onChange={value => setCourseCode(value)}
                disabled={!levelCode}
              >
                <Select.Option value="">
                  {t('common.text.all')}
                </Select.Option>
                {res?.data?.find(item => Number(item.courseCode) === levelCode)?.step?.map(item => (
                  <Select.Option
                    key={item.courseCode}
                    value={item.courseCode}
                  >
                    {item.courseName}
                  </Select.Option>
                ))}
              </Select>
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div className="content scrollbar">
        {isLoading2 && <Loader />}
        <ErrorResponse error={error2} />
        
        {
          res2?.length
            ? (
              <>
                <div className="comm-tab__line">
                  <ul>
                    <TabItem
                      currentContent={content}
                      onSetContent={setContent}
                      tabContent={Contents.Table}
                      tabLabel={t('statistics.studentEnrollMonth.tab1')}
                    />
                    <TabItem
                      currentContent={content}
                      onSetContent={setContent}
                      tabContent={Contents.Chart}
                      tabLabel={t('statistics.studentEnrollMonth.tab2')}
                    />
                  </ul>
                </div>
                <div>
                  {renderContent()}
                </div>
              </>
              )
            : isLoading2
              ? null
              : (
                <TableEmpty text={t('statistics.appday.text1')} />
                )
        }

        {multiCampusVisible &&
          <MultiCampusModal
            visible={multiCampusVisible}
            setVisible={setMultiCampusVisible}
            campusCodes={campusCodes}
            setCampusCodes={setCampusCodes}
          />
        }      
      </div>
    </>
  );
}

export default StatisticsStudentEnrollWeek;
